import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer
} from 'recharts'

/**
 * RiskCharts - Displays risk visualization charts
 *
 * @param {Object} visualizations - Visualization data from report
 */
const RiskCharts = memo(({ visualizations }) => {
	if (!visualizations) {
		return null
	}

	const { risk_distribution, risk_by_category, risk_trend } = visualizations

	// Risk distribution colors
	const RISK_COLORS = {
		high: '#DC2626',
		medium: '#F59E0B',
		low: '#10B981'
	}

	// Prepare pie chart data
	const pieData = useMemo(() => {
		if (!risk_distribution) return []
		return [
			{ name: 'High Risk', value: risk_distribution.high || 0, color: RISK_COLORS.high },
			{ name: 'Medium Risk', value: risk_distribution.medium || 0, color: RISK_COLORS.medium },
			{ name: 'Low Risk', value: risk_distribution.low || 0, color: RISK_COLORS.low }
		].filter(item => item.value > 0)
	}, [risk_distribution])

	// Prepare bar chart data
	const barData = useMemo(() => {
		if (!risk_by_category || !Array.isArray(risk_by_category)) return []
		return risk_by_category.map(item => ({
			category: item.category,
			score: item.score,
			projects: item.project_count || 0
		}))
	}, [risk_by_category])

	// Prepare line chart data
	const lineData = useMemo(() => {
		if (!risk_trend || !Array.isArray(risk_trend)) return []
		return risk_trend.map(item => ({
			date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
			score: item.score
		}))
	}, [risk_trend])

	const CustomPieTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0]
			const total = pieData.reduce((sum, item) => sum + item.value, 0)
			const percentage = ((data.value / total) * 100).toFixed(1)
			return (
				<div className="risk-charts__tooltip">
					<p style={{ color: data.payload.color }}>{data.name}</p>
					<p>{data.value} projects ({percentage}%)</p>
				</div>
			)
		}
		return null
	}

	const hasDistributionData = pieData.length > 0
	const hasCategoryData = barData.length > 0
	const hasTrendData = lineData.length > 0

	if (!hasDistributionData && !hasCategoryData && !hasTrendData) {
		return null
	}

	return (
		<div className="risk-charts">
			<div className="risk-charts__grid">
				{/* Risk Distribution Pie Chart */}
				{hasDistributionData && (
					<div className="risk-charts__card">
						<h4 className="risk-charts__card-title">Risk Distribution</h4>
						<div className="risk-charts__chart-container">
							<ResponsiveContainer width="100%" height={200}>
								<PieChart>
									<Pie
										data={pieData}
										cx="50%"
										cy="50%"
										innerRadius={50}
										outerRadius={80}
										paddingAngle={2}
										dataKey="value"
									>
										{pieData.map((entry, index) => (
											<Cell key={`cell-${index}`} fill={entry.color} />
										))}
									</Pie>
									<Tooltip content={<CustomPieTooltip />} />
								</PieChart>
							</ResponsiveContainer>
							<div className="risk-charts__legend">
								{pieData.map((item, index) => (
									<div key={index} className="risk-charts__legend-item">
										<span
											className="risk-charts__legend-color"
											style={{ backgroundColor: item.color }}
										/>
										<span className="risk-charts__legend-label">{item.name}</span>
										<span className="risk-charts__legend-value">{item.value}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				)}

				{/* Risk by Category Bar Chart */}
				{hasCategoryData && (
					<div className="risk-charts__card">
						<h4 className="risk-charts__card-title">Risk by Category</h4>
						<div className="risk-charts__chart-container">
							<ResponsiveContainer width="100%" height={200}>
								<BarChart data={barData} layout="vertical" margin={{ left: 20 }}>
									<CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
									<XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
									<YAxis
										dataKey="category"
										type="category"
										width={80}
										tick={{ fontSize: 12 }}
									/>
									<Tooltip
										formatter={(value, name) => [
											name === 'score' ? `${value}%` : value,
											name === 'score' ? 'Risk Score' : 'Projects'
										]}
									/>
									<Bar
										dataKey="score"
										fill="#3B82F6"
										radius={[0, 4, 4, 0]}
										barSize={20}
									/>
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>
				)}

				{/* Risk Trend Line Chart */}
				{hasTrendData && (
					<div className="risk-charts__card risk-charts__card--wide">
						<h4 className="risk-charts__card-title">Risk Trend Over Time</h4>
						<div className="risk-charts__chart-container">
							<ResponsiveContainer width="100%" height={200}>
								<LineChart data={lineData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="date" tick={{ fontSize: 12 }} />
									<YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
									<Tooltip
										formatter={(value) => [`${value}%`, 'Risk Score']}
									/>
									<Line
										type="monotone"
										dataKey="score"
										stroke="#3B82F6"
										strokeWidth={2}
										dot={{ r: 4, fill: '#3B82F6' }}
										activeDot={{ r: 6 }}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					</div>
				)}

				{/* Risk Heatmap Placeholder */}
				{hasCategoryData && (
					<div className="risk-charts__card">
						<h4 className="risk-charts__card-title">Risk Heatmap by Category</h4>
						<div className="risk-charts__heatmap">
							{barData.map((item, index) => {
								const level = item.score >= 70 ? 'high' : item.score >= 40 ? 'medium' : 'low'
								return (
									<div
										key={index}
										className={`risk-charts__heatmap-cell risk-charts__heatmap-cell--${level}`}
										title={`${item.category}: ${item.score}%`}
									>
										<span className="risk-charts__heatmap-label">{item.category}</span>
										<span className="risk-charts__heatmap-value">{item.score}%</span>
									</div>
								)
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	)
})

RiskCharts.displayName = 'RiskCharts'

RiskCharts.propTypes = {
	visualizations: PropTypes.shape({
		risk_distribution: PropTypes.shape({
			high: PropTypes.number,
			medium: PropTypes.number,
			low: PropTypes.number
		}),
		risk_by_category: PropTypes.arrayOf(
			PropTypes.shape({
				category: PropTypes.string,
				score: PropTypes.number,
				project_count: PropTypes.number
			})
		),
		risk_trend: PropTypes.arrayOf(
			PropTypes.shape({
				date: PropTypes.string,
				score: PropTypes.number
			})
		)
	})
}

export default RiskCharts
